import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Save, RotateCcw } from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// Phase 37 (piece 2) — admin "Case Team rights" switch-off list.
//
// Firm-level, per-key-per-bit: for each of the 86 case-internal rights, the admin
// sets whether a responsible partner may hand it out at case-team level. Quad
// bases render as a four-toggle row (View / Create / Edit / Delete); single-bit
// ops render as one toggle (mixed-shape rows). Ordered per CV/CCV subsection
// (reuses the Phase 37 catalog), each subsection individually collapsible and
// collapsed by default.
//
// There is NO pull-back on delist: delegability is a LIVE input to the server
// grant-validity predicate, so switching a right off makes existing RP-authored
// grants of it go dormant immediately (and revive if re-enabled). Admin-authored
// grants are unaffected.

// 145 §2 — THIS PANEL NEVER ASKED FOR A TRANSLATION.
//
// The group headings did (`t(g.subLabelKey, …)` below) and the bit toggles did
// (`t("caseTeamRights.bit_…")`), so the screen read as French headings over
// English rows — and over one literal `caseView.identity`, which is the string
// Laurent reported. The row labels were taken straight off the catalog's
// English (`m.displayLabel || m.label || m.key`) and rendered raw.
//
// The repair is a LOOKUP, not content: measured against the served catalog,
// all 37 rows this panel draws already have complete en/fr/nl `section.<key>`
// rows for the key they hold. Nothing is owed, nothing is invented.
//
// WHICH KEY, THOUGH — this is the part that had to be checked before wiring.
// A quad row is keyed by its BASE (`caseView.tasks`), and `section.<base>` is
// NOT what a translation is filed under: 8 of the 18 quad bases here have no
// such row, and nothing in the tree ever asks for one. What IS filed is the
// BIT key (`section.caseView.tasks.view` → "Tâches — consulter"). So a quad
// row resolves through one of its own bits and then drops the trailing action,
// exactly as the English path already did — and every quad's bits agree on the
// stem they leave behind (checked across all 18 bases × fr/nl: 0 disagree).
type RightRow =
  | { kind: "quad"; base: string; labelKey: string; labelFallback: string; bits: Record<string, string> }
  | { kind: "op"; key: string; labelKey: string; labelFallback: string; bit?: string | null };

type CatalogRow = {
  key: string;
  label?: string | null;
  displayLabel?: string | null;
  quadBase?: string | null;
};

// The registry's English, for when the catalog has not loaded — the same chain
// TeamAccessPanel.tsx:461 and RolesAdminPanel.tsx:530 use as their `fallback:`.
const fallbackOf = (m: CatalogRow) => m.displayLabel || m.label || m.key;

const BIT_ORDER = ["view", "create", "edit", "delete"];
const BIT_LABEL: Record<string, string> = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

function BitToggle({ bit, on, onClick }: { bit: string; on?: boolean; onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      title={t(`caseTeamRights.bit_${bit}`, { fallback: BIT_LABEL[bit] || bit })}
      style={{
        fontSize: FS.s9_5,
        fontWeight: 700,
        padding: "2px 8px",
        borderRadius: RAD.r10,
        minWidth: 54,
        textAlign: "center",
        background: on ? "rgba(34,197,94,.18)" : "rgba(245,158,11,.10)",
        color: on ? "var(--gn)" : "var(--am)",
        border: `1px solid ${on ? "rgba(34,197,94,.35)" : "rgba(245,158,11,.28)"}`,
        cursor: "pointer",
        fontFamily: "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {(on ? "✓ " : "✗ ") + t(`caseTeamRights.bit_${bit}`, { fallback: BIT_LABEL[bit] || bit })}
    </button>
  );
}

type CaseTeamRightsPanelProps = {
  toast?: (msg: string) => void;
  isAdmin?: boolean;
};

export default function CaseTeamRightsPanel({ toast, isAdmin }: CaseTeamRightsPanelProps) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<any>(null); // { sections, subsections }
  const [delegable, setDelegable] = useState<Record<string, any>>({}); // server resolved map
  const [draft, setDraft] = useState<Record<string, any>>({}); // local overrides not yet saved
  const [openSubs, setOpenSubs] = useState<Record<string, any>>({}); // collapsed by default
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setErr(null);
    try {
      const [cat, al] = await Promise.all([
        api<{ sections?: unknown[] } | null>("/permissions/catalog"),
        api<{ delegable?: Record<string, boolean> } | null>(
          "/firm-settings/case-team-allowlist",
        ),
      ]);
      setCatalog(cat && Array.isArray(cat.sections) ? cat : null);
      setDelegable(al && al.delegable ? al.delegable : {});
      setDraft({});
    } catch (e) {
      setErr((e as any).message || t("caseTeamRights.load_failed", { fallback: "Failed to load" }));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    // 163 §B.1 — the admin guard now lives BELOW the hooks (rules of hooks);
    // this effect keeps its old behaviour by not loading for non-admins.
    if (!isAdmin) return;
    reload(); /* eslint-disable-next-line */
  }, [isAdmin]);

  // Build groups: case-internal keys (CaseView + CollectiveCase), grouped by
  // subsection (catalog order), each subsection's rights grouped by quadBase /op.
  const groups = useMemo(() => {
    if (!catalog) return [];
    const ci = catalog.sections.filter((s: any) => s.isCaseInternal);
    const bySub: Record<string, any[]> = {};
    for (const m of ci) (bySub[m.subsection || "_misc"] ||= []).push(m);
    const out: any[] = [];
    for (const ss of catalog.subsections) {
      const items = bySub[ss.id];
      if (!items || !items.length) continue;
      // group into rights: quad base -> {label, bits:{bit:key}}, op -> single
      const rightsByBase = new Map();
      const rights: any[] = [];
      for (const m of items) {
        if (m.quadBase) {
          let r = rightsByBase.get(m.quadBase);
          if (!r) {
            // The KEY to look up, not a finished label: resolution happens at
            // render (rightLabel), so the row follows a language change instead
            // of freezing at whatever was loaded when this memo last ran.
            r = { kind: "quad", base: m.quadBase, labelKey: m.key, labelFallback: fallbackOf(m), bits: {} };
            rightsByBase.set(m.quadBase, r);
            rights.push(r);
          }
          r.bits[m.bit] = m.key;
        } else {
          rights.push({ kind: "op", key: m.key, labelKey: m.key, labelFallback: fallbackOf(m), bit: m.bit });
        }
      }
      out.push({ view: ss.view, subId: ss.id, subLabel: ss.label, subLabelKey: ss.labelKey, rights });
    }
    return out;
  }, [catalog]);

  // The row's visible name, resolved the way TeamAccessPanel.tsx:461 resolves
  // its own columns: t("section." + a key the catalog actually carries), with
  // the registry's English behind it. A quad row's key is one of its bits, so
  // the trailing action comes off after the lookup.
  const rightLabel = (r: RightRow) => {
    const s = t(`section.${r.labelKey}`, { fallback: r.labelFallback });
    return r.kind === "quad" ? dropAction(s) : s;
  };

  const isDelegable = (key: string) =>
    Object.prototype.hasOwnProperty.call(draft, key) ? draft[key] : delegable[key] === true;
  const toggle = (key: string) =>
    setDraft((d) => ({ ...d, [key]: !isDelegable(key) }));

  // Changed = draft entries that differ from the server's current resolved value.
  const changed = useMemo(
    () => Object.keys(draft).filter((k) => draft[k] !== (delegable[k] === true)),
    [draft, delegable],
  );

  // 163 §B.1 — the guard moved below the hooks; the hooks moved above the guard.
  if (!isAdmin) return null;

  async function save() {
    if (!changed.length) return;
    setSaving(true);
    try {
      const body: Record<string, any> = {};
      for (const k of changed) body[k] = draft[k];
      const res = await api<{ delegable?: Record<string, boolean> } | null>(
        "/firm-settings/case-team-allowlist",
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
      );
      setDelegable(res && res.delegable ? res.delegable : delegable);
      setDraft({});
      toast && toast(t("caseTeamRights.saved", { fallback: "Case Team rights saved" }));
    } catch (e) {
      toast && toast("⚠ " + ((e as any).message || t("common.save_failed", { fallback: "Save failed" })));
    }
    setSaving(false);
  }

  if (loading)
    return (
      <p style={{ fontSize: FS.s11, color: "var(--tm)", padding: "6px 0" }}>
        {t("common.loading", { fallback: "Loading…" })}
      </p>
    );
  if (err)
    return <p style={{ fontSize: FS.s11, color: "var(--rd)", padding: "6px 0" }}>⚠ {err}</p>;

  let lastView: any = null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ fontSize: FS.s10_5, color: "var(--tm)", margin: "0 0 2px", lineHeight: 1.5 }}>
        {t("caseTeamRights.intro", {
          fallback:
            "Choose which case-internal rights a responsible partner may hand out to a team member on a case. Admin grants are never restricted by this list. Switching a right off makes any partner-granted copies of it stop counting immediately (they revive if you switch it back on). Delete rights are off by default.",
        })}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {groups.map((g) => {
          const open = !!openSubs[g.subId];
          const total = g.rights.reduce(
            (n: number, r: any) => n + (r.kind === "quad" ? Object.keys(r.bits).length : 1),
            0,
          );
          const on = g.rights.reduce(
            (n: number, r: any) =>
              n +
              (r.kind === "quad"
                ? Object.values(r.bits).filter((k: any) => isDelegable(k)).length
                : isDelegable(r.key)
                  ? 1
                  : 0),
            0,
          );
          const viewBand = g.view !== lastView;
          lastView = g.view;
          return (
            <div key={g.subId}>
              {viewBand && (
                <div
                  style={{
                    fontSize: FS.s9_5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                    color: "var(--ts)",
                    margin: "8px 2px 2px",
                  }}
                >
                  {t(`permView.${g.view}`, {
                    fallback: g.view === "CollectiveCaseView" ? "Collective case" : "Case view",
                  })}
                </div>
              )}
              <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r8, overflow: "hidden" }}>
                <div
                  onClick={() => setOpenSubs((p) => ({ ...p, [g.subId]: !p[g.subId] }))}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    background: "rgba(74,124,255,.10)",
                    cursor: "pointer",
                  }}
                >
                  {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ fontSize: FS.s11, fontWeight: 600, color: "var(--tp)" }}>
                    {t(g.subLabelKey, { fallback: g.subLabel })}
                  </span>
                  <span style={{ fontSize: FS.s9_5, color: "var(--tm)" }}>
                    {t("caseTeamRights.delegable_count", withCount(on, "on", { total,
                      fallback: `${on} / ${total} delegable`,
                    }))}
                  </span>
                </div>
                {open && (
                  <div>
                    {g.rights.map((r: any) => (
                      <div
                        key={r.kind === "quad" ? r.base : r.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "5px 10px",
                          borderTop: "1px solid var(--br)",
                          fontSize: FS.s11,
                          flexWrap: "wrap",
                        }}
                      >
                        <span style={{ flex: 1, minWidth: 160, color: "var(--tp)" }}>
                          {rightLabel(r)}
                        </span>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {r.kind === "quad"
                            ? BIT_ORDER.filter((b) => r.bits[b]).map((b) => (
                                <BitToggle
                                  key={b}
                                  bit={b}
                                  on={isDelegable(r.bits[b])}
                                  onClick={() => toggle(r.bits[b])}
                                />
                              ))
                            : (
                                <BitToggle
                                  bit={r.bit || "grant"}
                                  on={isDelegable(r.key)}
                                  onClick={() => toggle(r.key)}
                                />
                              )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action bar */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          padding: "6px 0",
          borderTop: "1px solid var(--br)",
        }}
      >
        <span style={{ fontSize: FS.s10, color: changed.length ? "var(--am)" : "var(--tm)" }}>
          {changed.length
            ? t("caseTeamRights.unsaved", {
                count: changed.length,
                fallback: `● ${changed.length} unsaved change${changed.length === 1 ? "" : "s"}`,
              })
            : t("roles.all_saved", { fallback: "✓ All changes saved" })}
        </span>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ padding: "4px 12px", fontSize: FS.s11, marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4 }}
          disabled={!changed.length || saving}
          onClick={() => setDraft({})}
        >
          <RotateCcw size={11} />
          {t("roles.discard", { fallback: "Discard" })}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          style={{ padding: "4px 12px", fontSize: FS.s11, display: "inline-flex", alignItems: "center", gap: 4 }}
          disabled={!changed.length || saving}
          onClick={save}
        >
          <Save size={11} />
          {saving ? t("roles.saving", { fallback: "Saving…" }) : t("roles.save_changes", { fallback: "Save changes" })}
        </button>
      </div>
    </div>
  );
}

// Strip the trailing " — <action>" the registry appends to a quad's bit label,
// leaving the base's own name: "Tasks — edit" → "Tasks", "CC — Client list —
// view" → "CC — Client list".
//
// IT IS THE LAST EM-DASH SEGMENT, NOT THE LAST WORD, and that is the whole
// difference now that the string arriving here is translated. The version this
// replaces matched `\s+[—-]\s+\w+$`; `\w` is [A-Za-z0-9_], so it works on
// English and then splits the same screen in two the moment French arrives —
// "Agenda — supprimer" loses its action word while "Tâches — créer" keeps it,
// because `créer` does not end in an ASCII-only run reachable from the dash.
// Splitting on the em dash is the convention TeamAccessPanel.tsx:625-632
// already leans on and the seeded rows already keep, in all three languages.
const dropAction = (s: string) => s.replace(/\s+[—–]\s+[^—–]+$/, "").trim() || s;
